import { useCallback, useState } from 'react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type {
  DeepAccountDetail,
  CycleAccountsData,
  DeepInvoiceDetail,
  InvoiceCycle,
} from '../types';

export function useCycles() {
  const [cycles, setCycles] = useState<InvoiceCycle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosRubyClient.get('/api/invoice_health/cycles');
      setCycles(res.data.data.cycles || []);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load cycles',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return { cycles, loading, error, fetch };
}

export function useCycleAccounts() {
  const [data, setData] = useState<CycleAccountsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (params: { from: string; to: string; referenceTime: string }) => {
      setLoading(true);
      setError(null);

      try {
        const qs = new URLSearchParams();
        qs.append('from', params.from);
        qs.append('to', params.to);
        qs.append('reference_time', params.referenceTime);

        const res = await axiosRubyClient.get(
          `/api/invoice_health/cycle_accounts?${qs.toString()}`,
        );
        setData(res.data.data);
      } catch (err: any) {
        setError(
          err.response?.data?.errors?.join(', ') ||
            err.message ||
            'Failed to load cycle accounts',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, loading, error, fetch, clear };
}

export function useAddLineItem() {
  const [loadingTimesheetId, setLoadingTimesheetId] = useState<number | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const sync = useCallback(
    async (params: {
      timesheetId: number;
      from: string;
      to: string;
      referenceTime: string;
    }) => {
      setLoadingTimesheetId(params.timesheetId);
      setError(null);

      try {
        await axiosRubyClient.post(
          `/api/invoice_health/timesheets/${params.timesheetId}/add_line_item`,
          {
            from: params.from,
            to: params.to,
            reference_time: params.referenceTime,
          },
        );
        return { success: true };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to add line item';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingTimesheetId(null);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingTimesheetId, error, sync, clearError };
}

export function useRemoveLineItem() {
  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (invoiceItemId: number) => {
    setLoadingItemId(invoiceItemId);
    setError(null);

    try {
      await axiosRubyClient.delete(
        `/api/invoice_health/invoice_items/${invoiceItemId}`,
      );
      return { success: true };
    } catch (err: any) {
      const message =
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to remove line item';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoadingItemId(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingItemId, error, remove, clearError };
}

export type ReconcileAction = {
  invoice_id: number;
  account_id: number;
  customer_id: number;
  stripe_invoice_id: string;
  action: string;
  details: Record<string, unknown>;
};

export type ReconcilePreview = {
  mode: 'dry_run' | 'execute';
  invoices_scanned: number;
  actions: ReconcileAction[];
  stats: Record<string, number>;
};

export function useReconcile() {
  const [preview, setPreview] = useState<ReconcilePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = useCallback(
    async (
      accountId: number,
      cycleParams?: { from: string; to: string; referenceTime: string },
    ): Promise<{
      success: boolean;
      data?: ReconcilePreview;
      error?: string;
    }> => {
      setLoading(true);
      setError(null);
      setPreview(null);

      try {
        const qs = new URLSearchParams();
        if (cycleParams) {
          qs.append('from', cycleParams.from);
          qs.append('to', cycleParams.to);
          qs.append('reference_time', cycleParams.referenceTime);
        }
        const query = qs.toString();
        const url = `/api/invoice_health/accounts/${accountId}/reconcile${query ? `?${query}` : ''}`;
        const res = await axiosRubyClient.get(url);
        const data = res.data.data as ReconcilePreview;
        setPreview(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load reconcile preview';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const execute = useCallback(
    async (
      accountId: number,
      cycleParams?: { from: string; to: string; referenceTime: string },
    ): Promise<{
      success: boolean;
      data?: ReconcilePreview;
      error?: string;
    }> => {
      setExecuting(true);
      setError(null);

      try {
        const res = await axiosRubyClient.post(
          `/api/invoice_health/accounts/${accountId}/reconcile`,
          cycleParams
            ? {
                from: cycleParams.from,
                to: cycleParams.to,
                reference_time: cycleParams.referenceTime,
              }
            : {},
        );
        const data = res.data.data as ReconcilePreview;
        setPreview(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to execute reconciliation';
        setError(message);
        return { success: false, error: message };
      } finally {
        setExecuting(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  return { preview, loading, executing, error, fetchPreview, execute, clear };
}

export function useCreateInvoice() {
  const [loadingAccountId, setLoadingAccountId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (params: {
      accountId: number;
      referenceTime: string;
    }): Promise<{ success: boolean; error?: string }> => {
      setLoadingAccountId(params.accountId);
      setError(null);

      try {
        await axiosRubyClient.post(
          `/api/invoice_health/accounts/${params.accountId}/create_invoice`,
          { reference_time: params.referenceTime },
        );
        return { success: true };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to create invoice';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingAccountId(null);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingAccountId, error, create, clearError };
}

export function useVoidInvoice() {
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const voidInvoice = useCallback(async (invoiceId: number) => {
    setLoadingInvoiceId(invoiceId);
    setError(null);

    try {
      await axiosRubyClient.post(
        `/api/invoice_health/invoices/${invoiceId}/void`,
      );
      return true;
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to void invoice',
      );
      return false;
    } finally {
      setLoadingInvoiceId(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingInvoiceId, error, voidInvoice, clearError };
}

export function useInvoiceDetail() {
  const [invoiceDetail, setInvoiceDetail] = useState<DeepInvoiceDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inspectInvoice = useCallback(
    async (
      id: number,
    ): Promise<{
      success: boolean;
      data?: DeepInvoiceDetail;
      error?: string;
    }> => {
      setLoading(true);
      setError(null);
      setInvoiceDetail(null);

      try {
        const res = await axiosRubyClient.get(
          `/api/invoice_health/invoices/${id}`,
        );
        const data = res.data.data as DeepInvoiceDetail;
        setInvoiceDetail(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load invoice detail';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setInvoiceDetail(null);
    setError(null);
  }, []);

  return {
    invoiceDetail,
    loading,
    error,
    inspectInvoice,
    clear,
  };
}

export function useAccountDetail() {
  const [accountDetail, setAccountDetail] = useState<DeepAccountDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inspectAccount = useCallback(
    async (
      accountId: number,
      timesheetIds?: number[],
    ): Promise<{
      success: boolean;
      data?: DeepAccountDetail;
      error?: string;
    }> => {
      setLoading(true);
      setError(null);
      setAccountDetail(null);

      try {
        const qs = new URLSearchParams();
        if (timesheetIds && timesheetIds.length > 0) {
          qs.append('timesheet_ids', timesheetIds.join(','));
        }

        const query = qs.toString();
        const res = await axiosRubyClient.get(
          `/api/invoice_health/accounts/${accountId}${query ? `?${query}` : ''}`,
        );
        const data = res.data.data as DeepAccountDetail;
        setAccountDetail(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load account detail';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setAccountDetail(null);
    setError(null);
  }, []);

  return {
    accountDetail,
    loading,
    error,
    inspectAccount,
    clear,
  };
}
